export type AnalyticsWindow = 'today' | '7d' | '30d';

export const ANALYTICS_WINDOWS: Array<{ key: AnalyticsWindow; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
];

export interface UserRef {
  id: number;
  name: string;
}

export interface TileCounts {
  active_users: number;
  pages_opened: number;
}

export interface PageUser extends UserRef {
  days: number;
  last_visited: string;
}

export interface PageRow {
  path: string;
  module: string;
  unique_users: number;
  user_days: number;
  last_visited: string;
  users: PageUser[];
}

export interface IdleUser extends UserRef {
  last_seen: string | null;
}

// Shape of GET /admin/analytics. Dates are preformatted MM/DD/YYYY strings.
export interface AnalyticsSummary {
  window: AnalyticsWindow;
  from: string;
  to: string;
  tiles: Record<AnalyticsWindow, TileCounts>;
  daily_active_users: Array<{ date: string; count: number }>;
  modules: Array<{ module: string; users: number }>;
  idle_users: IdleUser[];
  pages: PageRow[];
}

export const plural = (count: number, singular: string, pluralForm = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

// Route ids mean nothing to an admin reading the dashboard; other params (e.g. :formAction) still name the page.
const ID_PARAM = /^:(id|\w+Id|\w+_id)$/;

const humanize = (segment: string): string =>
  segment
    .replace(/^:/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[_\-\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// '/backflow_tests/backflow_form/:id/:formAction' -> 'Backflow Tests - Backflow Form - Form Action'
export const pageLabel = (path: string): string =>
  path
    .split('/')
    .filter((segment) => segment && !ID_PARAM.test(segment))
    .map(humanize)
    .join(' - ');

// For the pages table, whose Module column already names the module: drop the leading segment only when it repeats
// the module ('Admin - Teams' -> 'Teams'). Kept when it differs, since it is what tells pages apart there
// ('Backflow Tests - …' vs 'Backflows - …' under Backflow Testing, 'Job Costs - New' under the Sales Estimates
// fallback), and when it is the whole name ('/warehouse').
export const pageName = ({ path, module }: Pick<PageRow, 'path' | 'module'>): string => {
  const label = pageLabel(path);
  const prefix = `${module} - `;
  return label.startsWith(prefix) ? label.slice(prefix.length) : label;
};

export interface PageSearch {
  page: string;
  module: string;
}

const contains = (text: string, term: string): boolean => text.toLowerCase().includes(term.trim().toLowerCase());

// Both searches must match. The page search matches the label people see and the raw path, for whoever knows the
// route; the module search matches only the module name, so "admin" doesn't pull in e.g. /collections/admin_notes.
export const filterPages = (pages: PageRow[], search: PageSearch): PageRow[] =>
  pages.filter(
    (page) => contains(`${pageLabel(page.path)} ${page.path}`, search.page) && contains(page.module, search.module),
  );
